import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { Quadro, Tarefa, Coluna, StatusTarefa, Usuario } from '../types';
import { quadroKanban } from '../data/mockData';
import tarefaAdminService, { TarefaAdminFiltros, RespostaPaginada } from '../services/tarefaAdminService';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

interface KanbanAdminContextType {
  quadro: Quadro;
  tarefas: Tarefa[];
  carregando: boolean;
  erro: string | null;
  filtros: TarefaAdminFiltros;
  totalTarefas: number;
  paginaAtual: number;
  totalPaginas: number;
  moverTarefa: (tarefaId: string, colunaOrigemId: string, colunaDestinoId: string) => void;
  atualizarTarefa: (tarefa: Tarefa) => Promise<void>;
  recarregarTarefas: (novosFiltros?: Partial<TarefaAdminFiltros>) => Promise<void>;
  setFiltros: (novosFiltros: Partial<TarefaAdminFiltros>) => void;
  carregarProximaPagina: () => Promise<void>;
}

const KanbanAdminContext = createContext<KanbanAdminContextType | undefined>(undefined);

interface KanbanAdminProviderProps {
  children: ReactNode;
}

export const KanbanAdminProvider: React.FC<KanbanAdminProviderProps> = ({ children }) => {
  const { usuario } = useAuth();
  const [quadro, setQuadro] = useState<Quadro>(quadroKanban);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [totalTarefas, setTotalTarefas] = useState<number>(0);
  const [paginaAtual, setPaginaAtual] = useState<number>(1);
  const [totalPaginas, setTotalPaginas] = useState<number>(1);
  const [filtros, setFiltrosState] = useState<TarefaAdminFiltros>({
    pagina: 1,
    limite: 10000, // Limite alto para buscar TODAS as tarefas ativas
    carregarConcluidas: true,
    diasConcluidas: 7 // Apenas concluídas dos últimos 7 dias
  });

  // Extrair todas as tarefas do quadro para fácil acesso
  const tarefas = React.useMemo(() => {
    return quadro.colunas.flatMap(coluna => coluna.tarefas);
  }, [quadro.colunas]);

  // Função para atualizar filtros e recarregar tarefas
  const setFiltros = (novosFiltros: Partial<TarefaAdminFiltros>) => {
    const filtrosAtualizados = { ...filtros, ...novosFiltros, pagina: 1 }; // Reset para página 1 ao mudar filtros
    setFiltrosState(filtrosAtualizados);
    recarregarTarefas(filtrosAtualizados);
  };

  // Função para carregar a próxima página
  const carregarProximaPagina = async () => {
    if (paginaAtual < totalPaginas) {
      const novosFiltros = { ...filtros, pagina: paginaAtual + 1 };
      setFiltrosState(novosFiltros);
      await recarregarTarefas(novosFiltros);
    }
  };

  // Função auxiliar para mapear tarefas da API para o formato do frontend
  const mapearTarefasDaAPI = (tarefasAPI: any[]): Tarefa[] => {
    return tarefasAPI.map(tarefa => {
      // Criar objetos de usuário simplificados compatíveis com a interface
      const responsavelSimplificado: Usuario = {
        id: tarefa.responsavel_id,
        nome: tarefa.responsavel_nome,
        email: tarefa.responsavel_email,
        cargo: '',  // Valores padrão para campos obrigatórios
        departamentoId: tarefa.departamento_id || '',
        ehLider: false,
        tipo: 'comum',
        avatar: '/profilepicture.png',
        tarefasAtivas: 0,
        tarefasConcluidas: 0,
        dataCriacao: ''
      };
      
      const criadorSimplificado: Usuario = {
        id: tarefa.criador_id,
        nome: tarefa.criador_nome,
        email: tarefa.criador_email,
        cargo: '',
        departamentoId: '',
        ehLider: false,
        tipo: 'comum',
        avatar: '/profilepicture.png',
        tarefasAtivas: 0,
        tarefasConcluidas: 0,
        dataCriacao: ''
      };
      
      return {
        id: tarefa.id,
        titulo: tarefa.titulo,
        descricao: tarefa.descricao,
        prioridade: tarefa.prioridade,
        status: tarefa.status,
        dataVencimento: tarefa.data_vencimento,
        dataNecessidade: tarefa.data_necessidade,
        dataCriacao: tarefa.data_criacao,
        dataConclusao: tarefa.data_conclusao,
        departamentoId: tarefa.departamento_id,
        departamento: {
          id: tarefa.departamento_id,
          nome: tarefa.departamento_nome,
          lideres: []
        },
        responsavel: responsavelSimplificado,
        criador: criadorSimplificado,
        comentarios: tarefa.comentarios || []
      };
    });
  };

  // Função para recarregar todas as tarefas da API
  const recarregarTarefas = async (novosFiltros?: Partial<TarefaAdminFiltros>) => {
    try {
      setCarregando(true);
      setErro(null);
      
      if (!usuario || usuario.tipo !== 'admin') {
        setErro('Acesso restrito a administradores');
        return;
      }
      
      // Usar filtros atualizados se fornecidos, caso contrário usar os filtros atuais
      const filtrosAtuais = novosFiltros || filtros;
      
      console.log('Carregando tarefas globais com filtros:', filtrosAtuais);
      
      const response = await tarefaAdminService.listarTarefasGlobais(filtrosAtuais);
      
      if (response && response.tarefas) {
        console.log(`Tarefas globais carregadas: ${response.tarefas.length}`);
        
        // Mapear os dados da API para o formato esperado pelo frontend
        const tarefasMapeadas = mapearTarefasDaAPI(response.tarefas);
        
        // Criar um quadro completamente novo com novas referências
        const novoQuadro = {
          ...quadro,
          colunas: quadro.colunas.map(coluna => ({
            ...coluna,
            tarefas: [] as Tarefa[] // Array completamente novo com tipo explícito
          })),
          _lastUpdate: Date.now() // Timestamp para forçar detecção de mudança
        };
        
        // Distribuir as tarefas nas colunas corretas
        tarefasMapeadas.forEach((tarefa: Tarefa) => {
          // Encontrar a coluna apropriada com base no status da tarefa
          let adicionada = false;
          
          for (const coluna of novoQuadro.colunas) {
            // Determinar quais status devem ser incluídos em cada coluna
            let statusPermitidos: string[] = [];
            
            switch(coluna.titulo) {
              // KANBAN GLOBAL usa estrutura de TAREFAS RECEBIDAS
              case 'Pendente':
                statusPermitidos = ['Pendente', 'Solicitado'];
                break;
              case 'A iniciar':
                statusPermitidos = ['A iniciar'];
                break;
              case 'Em Andamento':
                statusPermitidos = ['Em Andamento', 'Em Execução'];
                break;
              case 'Aguardando Aprovação':
                statusPermitidos = ['Aguardando Aprovação', 'Aguardando Resposta'];
                break;
              case 'Ajustes Requisitados':
                statusPermitidos = ['Ajustes Requisitados'];
                break;
              case 'Concluída':
                statusPermitidos = ['Concluída', 'Finalizada'];
                break;
              default:
                statusPermitidos = [coluna.titulo];
            }
            
            // Se o status da tarefa estiver na lista de status permitidos para esta coluna
            if (statusPermitidos.includes(tarefa.status)) {
              coluna.tarefas.push(tarefa);
              adicionada = true;
              break; // Sair do loop após adicionar a tarefa à coluna apropriada
            }
          }
          
          if (!adicionada) {
            console.warn(`Tarefa com status '${tarefa.status}' não foi adicionada a nenhuma coluna:`, tarefa);
          }
        });
        
        setQuadro(novoQuadro);
        setTotalTarefas(response.total);
        setPaginaAtual(response.pagina);
        setTotalPaginas(response.totalPaginas);
      } else {
        setErro('Erro ao recarregar tarefas');
      }
    } catch (error) {
      console.error('Erro ao carregar tarefas globais:', error);
      setErro('Erro ao carregar tarefas do servidor');
      toast.error('Erro ao carregar tarefas. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  };

  const moverTarefa = (tarefaId: string, colunaOrigemId: string, colunaDestinoId: string) => {
    console.log('Função moverTarefa chamada com:', { tarefaId, colunaOrigemId, colunaDestinoId });
    
    // Criar uma cópia profunda do quadro atual
    const quadroAtual = JSON.parse(JSON.stringify(quadro));
    
    // Encontrar a tarefa em qualquer coluna do quadro
    let tarefaEncontrada: Tarefa | undefined;
    let colunaOrigemReal: Coluna | undefined;
    let indexTarefaOrigem = -1;
    
    // Procurar a tarefa em todas as colunas
    for (const coluna of quadroAtual.colunas) {
      const index = coluna.tarefas.findIndex((t: Tarefa) => t.id === tarefaId);
      if (index !== -1) {
        tarefaEncontrada = { ...coluna.tarefas[index] };
        colunaOrigemReal = coluna;
        indexTarefaOrigem = index;
        break;
      }
    }
    
    if (!tarefaEncontrada || !colunaOrigemReal || indexTarefaOrigem === -1) {
      console.error('Tarefa não encontrada em nenhuma coluna:', tarefaId);
      return;
    }
    
    // Encontrar a coluna de destino pelo ID
    const colunaDestino = quadroAtual.colunas.find((col: Coluna) => col.id === colunaDestinoId);
    if (!colunaDestino) {
      console.error('Coluna de destino não encontrada:', colunaDestinoId);
      return;
    }
    
    // Remover a tarefa da coluna de origem
    colunaOrigemReal.tarefas.splice(indexTarefaOrigem, 1);
    
    // Atualizar o status da tarefa para o título da coluna de destino
    tarefaEncontrada.status = colunaDestino.titulo as StatusTarefa;
    
    // Adicionar a tarefa à coluna de destino
    colunaDestino.tarefas.push(tarefaEncontrada);
    
    console.log('Quadro atualizado:', quadroAtual);
    setQuadro(quadroAtual);
    
    // Atualizar a tarefa na API
    atualizarTarefa(tarefaEncontrada);
  };

  const atualizarTarefa = async (tarefaAtualizada: Tarefa) => {
    try {
      // Atualizar localmente primeiro para feedback imediato
      const novasColunas = quadro.colunas.map(coluna => {
        const tarefaIndex = coluna.tarefas.findIndex(t => t.id === tarefaAtualizada.id);
        if (tarefaIndex !== -1) {
          // Verificar se a tarefa deve permanecer na coluna atual
          if (coluna.statusPermitidos.includes(tarefaAtualizada.status)) {
            // Atualizar na coluna atual
            const novasTarefas = [...coluna.tarefas];
            novasTarefas[tarefaIndex] = tarefaAtualizada;
            return { ...coluna, tarefas: novasTarefas };
          } else {
            // Remover da coluna atual se o status não é compatível
            const novasTarefas = coluna.tarefas.filter(t => t.id !== tarefaAtualizada.id);
            return { ...coluna, tarefas: novasTarefas };
          }
        }
        return coluna;
      });

      // Verificar se a tarefa já está em alguma coluna compatível
      let tarefaAdicionada = false;
      for (const coluna of novasColunas) {
        if (coluna.tarefas.some(t => t.id === tarefaAtualizada.id)) {
          tarefaAdicionada = true;
          break;
        }
      }

      // Se a tarefa não foi adicionada a nenhuma coluna, encontrar a coluna correta
      if (!tarefaAdicionada) {
        // Tentar encontrar uma coluna com título exato igual ao status
        let colunaDestino = novasColunas.find(col => col.titulo === tarefaAtualizada.status);
        
        // Se não encontrar, procurar por colunas compatíveis com o status
        if (!colunaDestino) {
          for (const coluna of novasColunas) {
            if (coluna.statusPermitidos.includes(tarefaAtualizada.status)) {
              colunaDestino = coluna;
              break;
            }
          }
        }

        // Adicionar à coluna de destino se encontrada
        if (colunaDestino) {
          colunaDestino.tarefas.push(tarefaAtualizada);
        }
      }

      setQuadro({
        ...quadro,
        colunas: novasColunas
      });
      
      // Enviar a atualização para a API
      // IMPORTANTE: Não enviar título, descrição e prioridade (campos bloqueados)
      const response = await fetch(`/api/tarefas/${tarefaAtualizada.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('bravobet_token')}`
        },
        body: JSON.stringify({
          status: tarefaAtualizada.status,
          responsavel_id: tarefaAtualizada.responsavel?.id,
          data_vencimento: tarefaAtualizada.dataVencimento
        })
      });
      
      if (!response.ok) {
        throw new Error('Erro ao atualizar tarefa');
      }
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
      toast.error('Erro ao atualizar tarefa. Tente novamente.');
      // Recarregar tarefas para garantir consistência
      await recarregarTarefas();
    }
  };

  // Carregar tarefas da API ao iniciar
  useEffect(() => {
    if (usuario && usuario.tipo === 'admin') {
      recarregarTarefas();
    }
  }, [usuario?.id]);

  return (
    <KanbanAdminContext.Provider value={{
      quadro,
      tarefas,
      carregando,
      erro,
      filtros,
      totalTarefas,
      paginaAtual,
      totalPaginas,
      moverTarefa,
      atualizarTarefa,
      recarregarTarefas,
      setFiltros,
      carregarProximaPagina
    }}>
      {children}
    </KanbanAdminContext.Provider>
  );
};

export const useKanbanAdmin = (): KanbanAdminContextType => {
  const context = useContext(KanbanAdminContext);
  if (context === undefined) {
    throw new Error('useKanbanAdmin deve ser usado dentro de um KanbanAdminProvider');
  }
  return context;
};
